/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';

import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';
import BasicDropdown from 'ember-basic-dropdown/addon/components/basic-dropdown';
import { FlyMouseEvent } from '@customerio/fly-components/types/fly-components/events';

interface AddLanguageVariationsArgs {
  editableEntity: ComposerEditableEntity;
  onSave: () => unknown;
  hasReadOnlyPermission: boolean;
  type?: string;
}

export default class AddLanguageVariations extends Component<AddLanguageVariationsArgs> {
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service analytics!: AnalyticsService;

  @tracked isShowingConfirmEditorModal = false;
  @tracked isShowingManageLanguagesModal = false;

  get disableAddLanguageVariationForRedactedAuthor() {
    return this.args.type === 'webhook' && this.auth.redacted;
  }

  @action
  trackAddLanguagesClicked() {
    if (!this.args.hasReadOnlyPermission) {
      this.analytics.track('languages', 'add_language_clicked', this.args.editableEntity.analyticsData);
    }
  }

  saveAndUnblockTranslation = task(async (dropdown: any) => {
    await this.args.onSave();
    this.isShowingConfirmEditorModal = false;
    dropdown.actions.open();
  });

  @action
  onLanguagesDropdownOpen() {
    if (this.args.hasReadOnlyPermission) {
      return false;
    }

    if (this.args.editableEntity.mustSaveBeforeAddingLanguages) {
      this.isShowingConfirmEditorModal = true;
      return false;
    }

    if (!this.environment.current.hasLanguageAttribute) {
      this.isShowingManageLanguagesModal = true;
      return false;
    }

    return true;
  }

  // BasicDropdown component captures and handles "click" events before they
  // bubble up. This can lead to unexpected behavior in some cases.
  // Example: a dropdown has a tooltip, and a tooltip has a link. Clicking on the
  // link will cause the dropdown to be destroyed before the tooltip link "click"
  // event is properly handled.
  @action
  onLanguagesDropdownClose(_dropdown: BasicDropdown, event?: FlyMouseEvent<HTMLElement>) {
    if (event?.target == null) {
      return true;
    }
    return !event.target.hasAttribute('data-prevent-parent-basic-dropdown-close');
  }
}
